import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, timer, switchMap, catchError, of } from 'rxjs';
import { environment } from '../../environments/environment';

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  phone: string;
  user_type: 'Company/brand' | 'Agency';
  business_title?: string;
  role: string; // Added role field
  email_verified: boolean;
  is_active: boolean;
  last_login: string | null;
  created_at: string;
  settings?: { show_section_reload_button?: boolean };
}

export interface UserSettings {
  show_section_reload_button: boolean;
}

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  user_type: 'Company/brand' | 'Agency';
  business_title?: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  tokens: AuthTokens;
}

export interface RegisterResponse {
  user: User;
  tokens: AuthTokens;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly API_URL = environment.apiUrl;
  private readonly TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_KEY = 'current_user';

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  constructor(
    private http: HttpClient
  ) {
    this.initializeAuth();
  }

  private initializeAuth(): void {
    const token = this.getToken();
    const user = this.getCurrentUser();

    if (token && user) {
      this.currentUserSubject.next(user);
      this.isAuthenticatedSubject.next(true);
    }
  }

  // Registration
  register(request: RegisterRequest): Observable<RegisterResponse> {
    return this.http.post<RegisterResponse>(`${this.API_URL}/auth/register`, request)
      .pipe(
        tap(response => {
          this.handleAuthSuccess(response.user, response.tokens);
        })
      );
  }

  // Login
  login(request: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.API_URL}/auth/login`, request)
      .pipe(
        tap(response => {
          this.handleAuthSuccess(response.user, response.tokens);
        })
      );
  }

  // Forgot password (self-service). Sends frontend's frontendUrl so reset link in email matches the app origin.
  forgotPassword(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API_URL}/auth/forgot-password`, {
      email,
      frontend_url: environment.frontendUrl
    });
  }

  // Reset password with token from email
  resetPassword(token: string, newPassword: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API_URL}/auth/reset-password`, { token, new_password: newPassword });
  }

  // Logout
  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);

    this.currentUserSubject.next(null);
    this.isAuthenticatedSubject.next(false);
  }

  // Get current user
  getCurrentUser(): User | null {
    const userStr = localStorage.getItem(this.USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }

  // Get access token
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  // Get refresh token
  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  // Check if token is expired
  isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= expiry;
    } catch {
      return true;
    }
  }

  // Get token expiration time
  getTokenExpirationTime(): Date | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return new Date(payload.exp * 1000);
    } catch {
      return null;
    }
  }

  // Get time until token expires
  getTimeUntilExpiry(): number {
    const expiryTime = this.getTokenExpirationTime();
    if (!expiryTime) return 0;

    return expiryTime.getTime() - Date.now();
  }

  // Start automatic token refresh
  private startTokenRefresh(): void {
    // Check token every 5 minutes and refresh if needed
    timer(0, 5 * 60 * 1000).pipe(
      switchMap(() => {
        if (this.isTokenExpired() || this.isTokenExpiringSoon()) {
          return this.refreshToken();
        }
        return of(null);
      }),
      catchError(error => {
        console.error('Token refresh failed:', error);
        // If refresh fails, logout user
        this.logout();
        return of(null);
      })
    ).subscribe();
  }

  private isTokenExpiringSoon(): boolean {
    const token = this.getToken();
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeUntilExpiry = expiry - now;

      // Refresh if token expires in less than 30 minutes
      return timeUntilExpiry < (30 * 60 * 1000);
    } catch {
      return true;
    }
  }

  // Refresh token
  refreshToken(): Observable<any> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.logout();
      return of(null);
    }

    return this.http.post(`${this.API_URL}/auth/refresh`, { refresh_token: refreshToken })
      .pipe(
        tap((response: any) => {
          if (response.access_token) {
            localStorage.setItem(this.TOKEN_KEY, response.access_token);
            // console.log('Token refreshed successfully');
          }
        }),
        catchError(error => {
          console.error('Token refresh failed:', error);
          this.logout();
          return of(null);
        })
      );
  }

  // Get user profile
  getUserProfile(): Observable<User> {
    return this.http.get<User>(`${this.API_URL}/auth/me`);
  }

  private handleAuthSuccess(user: User, tokens: AuthTokens): void {
    localStorage.setItem(this.TOKEN_KEY, tokens.access_token);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, tokens.refresh_token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));

    this.currentUserSubject.next(user);
    this.isAuthenticatedSubject.next(true);

    // Log token expiration info
    const expiryTime = this.getTokenExpirationTime();
    if (expiryTime) {
      // console.log('🔐 Login successful!');
      // console.log('👤 User:', user.email);
      // console.log('⏰ Token expires at:', expiryTime.toLocaleString());
      // console.log('⏱️  Token valid for:', Math.round(this.getTimeUntilExpiry() / (1000 * 60)), 'minutes');
    }

    // Start automatic token refresh
    this.startTokenRefresh();
  }

  // Update user profile
  updateProfile(updates: Partial<User>): Observable<User> {
    return this.http.put<User>(`${this.API_URL}/auth/profile`, updates)
      .pipe(
        tap(updatedUser => {
          localStorage.setItem(this.USER_KEY, JSON.stringify(updatedUser));
          this.currentUserSubject.next(updatedUser);
        })
      );
  }

  // Change password
  changePassword(currentPassword: string, newPassword: string): Observable<any> {
    return this.http.post(`${this.API_URL}/auth/change-password`, {
      current_password: currentPassword,
      new_password: newPassword
    });
  }

  // Role-based helper methods
  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user ? user.role === role : false;
  }

  isAdmin(): boolean {
    return this.hasRole('Admin');
  }

  isUniversity(): boolean {
    return this.hasRole('University');
  }

  isMember(): boolean {
    return this.hasRole('Member');
  }

  canAccessUserManagement(): boolean {
    return this.isAdmin();
  }

  canAccessRoleManagement(): boolean {
    return this.isAdmin();
  }

  /** GET /users/me/settings */
  getUserSettings(): Observable<UserSettings> {
    const base = this.API_URL.endsWith('/') ? this.API_URL.slice(0, -1) : this.API_URL;
    return this.http.get<UserSettings>(`${base}/users/me/settings`);
  }

  /** PATCH /users/me/settings */
  updateUserSettings(settings: Partial<UserSettings>): Observable<UserSettings> {
    const base = this.API_URL.endsWith('/') ? this.API_URL.slice(0, -1) : this.API_URL;
    return this.http.patch<UserSettings>(`${base}/users/me/settings`, settings);
  }

  canAccessProjectManagement(): boolean {
    return this.isAdmin() || this.isUniversity() || this.isMember();
  }
}

